import React from "react";
import { mergeStyles } from '@fluentui/merge-styles';

export interface ModeTab<TKey extends string> {
    key: TKey;
    text: string;
    icon?: React.ReactNode;
}

type ModeTabsSize = "xs" | "sm" | "md" | "lg";

interface Props<TKey extends string> {
    tabs: ModeTab<TKey>[];
    selectedKey: TKey;
    onChange: (key: TKey) => void;
    ariaLabel: string;
    size?: ModeTabsSize;
    className?: string;
}

const tabsList = mergeStyles({
    display: 'inline-flex',
    boxSizing: 'border-box',
    alignItems: 'center',
    gap: 'var(--druid-tabs-gap)',
    padding: 'var(--druid-tabs-list-padding)',
    borderRadius: 'var(--druid-tabs-list-radius)',
    boxShadow: 'inset 0 0 0 1px var(--druid-tabs-list-border-color)',
});

const tabButton = (size: ModeTabsSize, selected: boolean) => mergeStyles({
    display: 'inline-flex',
    height: `var(--druid-tab-button-min-height-${size})`,
    minHeight: `var(--druid-tab-button-min-height-${size})`,
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--druid-tab-button-gap)',
    margin: 0,
    padding: `var(--druid-tab-button-padding-y-${size}) var(--druid-tab-button-padding-x-${size})`,
    border: 0,
    borderRadius: `var(--druid-tab-button-radius-${size})`,
    background: selected ? 'var(--druid-tab-button-success-active-bg)' : 'transparent',
    color: selected ? 'var(--druid-tab-button-active-text-color)' : 'var(--druid-tab-button-neutral-color)',
    fontFamily: 'inherit',
    fontSize: `1rem`,
    fontWeight: selected ? 'var(--druid-font-weight-semibold)' : 'var(--druid-tab-button-font-weight)',
    lineHeight: `var(--druid-tab-button-line-height-${size})`,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background-color var(--druid-transition-fast) ease, color var(--druid-transition-fast) ease',
    selectors: {
        ':hover': selected ? {} : {
            backgroundColor: 'var(--druid-tab-button-success-hover-bg)',
            color: 'var(--druid-tab-button-hover-text-color)',
        },
        ':focus-visible': {
            outline: '2px solid var(--druid-button-focus-border)',
            outlineOffset: '1px',
        },
    },
});

const tabIcon = (size: ModeTabsSize, selected: boolean) => mergeStyles({
    display: 'inline-flex',
    alignItems: 'center',
    flex: `0 0 var(--druid-tab-button-icon-size-${size})`,
    fontSize: `var(--druid-tab-button-icon-size-${size})`,
    lineHeight: '1',
    color: selected ? 'var(--druid-tab-button-success-active-color)' : 'inherit',
});

export function EditorModeTabs<TKey extends string>(props: Props<TKey>) {
    const { tabs, selectedKey, onChange, ariaLabel, size = "sm", className } = props;

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
        if (!step) {
            return;
        }
        event.preventDefault();
        const current = tabs.findIndex(t => t.key === selectedKey);
        const next = tabs[(current + step + tabs.length) % tabs.length];
        if (next && next.key !== selectedKey) {
            onChange(next.key);
        }
    };

    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            className={`${tabsList}${className ? ` ${className}` : ""}`}
            onKeyDown={onKeyDown}
        >
            {tabs.map(tab => {
                const selected = tab.key === selectedKey;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        tabIndex={selected ? 0 : -1}
                        className={tabButton(size, selected)}
                        onClick={() => !selected && onChange(tab.key)}
                    >
                        {tab.icon && (
                            <span className={tabIcon(size, selected)} aria-hidden={true}>{tab.icon}</span>
                        )}
                        {tab.text}
                    </button>
                );
            })}
        </div>
    );
}

export default EditorModeTabs;
